interface PaymentResult {
  status: 'success' | 'error';
  error?: string;
  transactionId?: string;
}

export const initiateCardPayment = async (
  amount: number,
  currency: string
): Promise<PaymentResult> => {
  try {
    // Validation des paramètres
    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error('invalid_amount');
    }
    if (!currency || typeof currency !== 'string' || currency.length !== 3) {
      throw new Error('invalid_currency');
    }

    // Format amount to 2 decimal places and ensure it's a string
    const formattedAmount = amount.toFixed(2);
    
    // Build the deep link with all required parameters
    const deepLink = encodeURI(
      `thermalprinter://payment?` +
      `amount=${formattedAmount}&` +
      `currency=${currency}&` +
      `callbackUrl=myapp://payment/result&` +
      `timestamp=${Date.now()}`
    );

    console.log('[PaymentUtils] Initiating payment:', { amount: formattedAmount, currency });

    // Check if the URL is valid
    try {
      new URL(deepLink);
    } catch {
      throw new Error('app_not_installed');
    }

    // Create a promise that will handle the payment flow
    return new Promise((resolve, reject) => {
      let hasResponded = false;
      let timeoutId: NodeJS.Timeout;

      // Handler for the URL event
      const handleMessage = (event: MessageEvent) => {
        if (hasResponded || !event.data || typeof event.data !== 'string') return;
        
        console.log('[PaymentUtils] Received payment response:', event.data);
        
        try {
          // Parse the URL and extract parameters
          const parsedUrl = new URL(event.data);
          const status = parsedUrl.searchParams.get('status');
          const error = parsedUrl.searchParams.get('error');
          const transactionId = parsedUrl.searchParams.get('transaction_id');

          // Clear timeout since we got a response
          if (timeoutId) clearTimeout(timeoutId);
          
          // Mark as responded to prevent duplicate handling
          hasResponded = true;
          
          // Remove the event listener
          window.removeEventListener('message', handleMessage);

          // Handle the response
          if (status === 'success') {
            resolve({ 
              status: 'success',
              transactionId 
            });
          } else {
            reject({ 
              status: 'error', 
              error: error || 'payment_failed',
              transactionId
            });
          }
        } catch (error) {
          console.error('[PaymentUtils] Error handling payment response:', error);
          reject({ 
            status: 'error', 
            error: 'invalid_callback' 
          });
        }
      };

      // Set up message handling
      window.addEventListener('message', handleMessage);

      // Open payment URL
      window.location.href = deepLink;

      // Set up timeout
      timeoutId = setTimeout(() => {
        if (hasResponded) return;
        
        console.log('[PaymentUtils] Payment timeout');
        hasResponded = true;
        window.removeEventListener('message', handleMessage);
        reject({ 
          status: 'error', 
          error: 'timeout' 
        });
      }, 2 * 60 * 1000); // 2 minutes timeout
    });
  } catch (error) {
    console.error('[PaymentUtils] Error in initiateCardPayment:', error);
    return { 
      status: 'error',
      error: error instanceof Error ? error.message : 'unknown_error'
    };
  }
};

// Utility to check if the payment app is installed
export const checkPaymentAppAvailability = async (): Promise<boolean> => {
  try {
    // Try to create a URL object to validate the deep link
    new URL('thermalprinter://');
    return true;
  } catch (error) {
    console.error('[PaymentUtils] Error checking payment app:', error);
    return false;
  }
};